import { employeeService } from "../api/services";
import { useAccount, useMsal } from "@azure/msal-react";
import { useQuery } from "react-query";

// Helper to decode Google JWT and extract user info
const decodeGoogleCredential = (credential) => {
  try {
    const base64Url = credential.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding Google credential:', error);
    return null;
  }
};

export default function useUserProfile() {
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] || {});

  // Check for Google auth
  const authProvider = localStorage.getItem('authProvider');
  const googleCredential = localStorage.getItem('googleCredential');
  const isGoogleAuth = authProvider === 'google' && googleCredential;

  // Get email from either Microsoft or Google auth
  const getEmailFromAuth = () => {
    if (isGoogleAuth) {
      const decoded = decodeGoogleCredential(googleCredential);
      return decoded?.email?.toLowerCase();
    }
    return account?.username?.toLowerCase() || account?.email?.toLowerCase();
  };

  const email = getEmailFromAuth();
  const isEnabled = isGoogleAuth ? !!email : (!!account && !!account.username);

  const { isSuccess, data: userProfile, isLoading, error } = useQuery(
    ["useUserProfile", email],
    async () => {
      if (!email) {
        console.error('No email found in account');
        return {
          employeeNo: null,
          email: null,
        };
      }

      try {
        console.log('%c🔍 Fetching employee profile for email:', 'color: #3b82f6; font-weight: bold', email);

        // Query Dynamics 365 API to find employee by email
        const { data } = await employeeService.getEmployeeByEmail(email);

        if (data.value && data.value.length > 0) {
          const employee = data.value[0];
          console.log('%c✅ Employee found:', 'color: #10b981; font-weight: bold', employee);

          // Store employee data in sessionStorage
          sessionStorage.setItem('employeeNo', employee.PersonnelNumber || employee.employeeNo || employee.number);
          sessionStorage.setItem('employeeName', employee.displayName || employee.fullName || employee.DisplayName);

          return {
            employeeNo: employee.PersonnelNumber || employee.employeeNo || employee.number,
            displayName: employee.Name || employee.displayName || employee.fullName || employee.DisplayName,
            email: employee.PrimaryContactEmail || employee.companyEmail || employee.CompanyEmail || email,
            ...employee,
          };
        }

        console.warn('%c⚠️ No employee found for email:', 'color: #f59e0b; font-weight: bold', email);
        return {
          employeeNo: null,
          email: email,
        };
      } catch (error) {
        console.error('%c❌ Error fetching employee profile:', 'color: #ef4444; font-weight: bold', error);
        return {
          employeeNo: null,
          email: email,
          error: error.message,
        };
      }
    },
    {
      enabled: isEnabled, // Run query if we have email from either auth provider
      retry: 2, // Retry twice on failure
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    }
  );

  if (isLoading) {
    return { isLoading: true };
  }

  if (error) {
    return { employeeNo: null, error };
  }

  if (isSuccess) {
    return userProfile;
  }

  return {};
}
